import React, { useState } from 'react';
import { BookOpen, Users, Award, HelpCircle, FileText, Video, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const TrainingModule = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  // Get user role from localStorage to determine back navigation
  const userRole = sessionStorage.getItem('userRole');

  const handleBackToDashboard = () => {
    if (userRole === 'psychologist') {
      navigate('/psychologist-dashboard');
    } else if (userRole === 'client') {
      navigate('/client-dashboard');
    } else {
      // Fallback to login if role not found
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={handleBackToDashboard}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <BookOpen className="w-12 h-12 text-white" />
            <div>
              <h1 className="text-3xl font-bold text-white">INSIGHT Platform Training Module</h1>
              <p className="text-blue-100 mt-2">Comprehensive Guide for Clients & Psychologists</p>
            </div>
          </div>
          <div className="flex gap-4 mt-6 flex-wrap">
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <p className="text-white font-semibold">Version 3.0 - Phase 5 AI Features</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <p className="text-white font-semibold">November 2025</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <p className="text-white font-semibold">DPDPA Compliant</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid grid-cols-3 lg:grid-cols-9 gap-2 h-auto mb-6">
            <TabsTrigger value="overview" className="text-sm">Overview</TabsTrigger>
            <TabsTrigger value="client" className="text-sm">Client Guide</TabsTrigger>
            <TabsTrigger value="psychologist" className="text-sm">Psychologist Guide</TabsTrigger>
            <TabsTrigger value="phases" className="text-sm">5 Phases</TabsTrigger>
            <TabsTrigger value="features" className="text-sm">Features</TabsTrigger>
            <TabsTrigger value="phase5-ai" className="text-sm font-bold text-blue-600">Phase 5 AI Features</TabsTrigger>
            <TabsTrigger value="best-practices" className="text-sm">Best Practices</TabsTrigger>
            <TabsTrigger value="troubleshooting" className="text-sm">Troubleshooting</TabsTrigger>
            <TabsTrigger value="quick-ref" className="text-sm">Quick Reference</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                    What is INSIGHT?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">
                    INSIGHT is a DPDPA-compliant, comprehensive Structured Qualitative Interpretative Summary platform 
                    designed to facilitate structured psychological exploration and thematic understanding.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                      <h3 className="font-semibold text-blue-900 mb-2">Core Purpose</h3>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Conduct systematic 5-phase structured qualitative interpretative summary</li>
                        <li>• Generate AI-powered thematic synthesis Analysis</li>
                        <li>• Provide evidence-based therapeutic techniques and resources</li>
                        <li>• Enable secure psychologist-client collaboration</li>
                      </ul>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                      <h3 className="font-semibold text-green-900 mb-2">Key Features</h3>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>✓ 5-Phase Structured Qualitative Interpretative Summary Process</li>
                        <li>✓ 14 Points SQIS</li>
                        <li>✓ AI-Generated Thematic Analysis</li>
                        <li>✓ Evidence-Based Therapeutic Techniques</li>
                        <li>✓ Secure Video Consultations</li>
                        <li>✓ 90-Day Analysis Validity</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-600" />
                    User Roles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-bold text-lg text-blue-600 mb-3">Client Role</h3>
                      <p className="text-sm text-gray-600 mb-3">Limited, privacy-focused access</p>
                      <ul className="text-sm space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Complete structured qualitative interpretative summary phases
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          View their structured qualitative interpretative summary Analysis
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Book consultations
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Reuse valid analysis (90 days)
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-bold text-lg text-indigo-600 mb-3">Psychologist Role</h3>
                      <p className="text-sm text-gray-600 mb-3">Full clinical access</p>
                      <ul className="text-sm space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Review client thematic data
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Generate AI thematic Analysis
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Create custom synthesis
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Generate evidence-based therapeutic techniques
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          Conduct video sessions
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Client Guide Tab */}
          <TabsContent value="client">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Getting Started as a Client</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Step 1: Registration & Login</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Navigate to the INSIGHT platform</li>
                      <li>Click "Sign Up" as a Client</li>
                      <li>Provide required information (Name, Email, Password)</li>
                      <li>Verify email (if required)</li>
                      <li>Login with your credentials</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">Step 2: Booking Your First Consultation</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Navigate to Client Dashboard</li>
                      <li>Click "Book New Consultation"</li>
                      <li>Select a psychologist from the list</li>
                      <li>Review psychologist profile (qualifications, specializations)</li>
                      <li>Click "Book Consultation" to begin structured qualitative interpretative summary</li>
                    </ol>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-amber-900 mb-1">Before Starting Structured Qualitative Interpretative Summary</h4>
                        <ul className="text-sm text-amber-800 space-y-1">
                          <li>✓ Set aside 2 hours uninterrupted time</li>
                          <li>✓ Find quiet, private space</li>
                          <li>✓ Use desktop/laptop (not mobile)</li>
                          <li>✓ Ensure stable internet connection</li>
                          <li>✓ Be in right mindset (calm, focused)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Phase Guides for Clients */}
              <Card>
                <CardHeader>
                  <CardTitle>Completing the 5-Phase Structured Qualitative Interpretative Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    {
                      phase: 1,
                      title: "Client Basic Details",
                      time: "15-20 minutes",
                      description: "Gather comprehensive demographic and background information",
                      fields: ["Personal Information (name, gender, DOB)", "Family Structure", "Health Information", "Goals (up to 10)", "Reason for Consultation (min 20 characters)"],
                      tips: ["Be honest and thorough", "Provide detailed descriptions", "Date of birth must be past date", "All fields except Goals 2-10 are mandatory"]
                    },
                    {
                      phase: 2,
                      title: "Moral Values Analysis",
                      time: "30-45 minutes",
                      description: "Explore your moral values and decision-making patterns through thematic scenarios",
                      fields: ["Moral dilemma questions", "Multiple-choice and open-ended responses", "Questions about values and beliefs"],
                      tips: ["Answer honestly - no 'right' answers", "Take time to think through scenarios", "Explain your reasoning", "Be consistent with your values"]
                    },
                    {
                      phase: 3,
                      title: "structured Word Association Test",
                      time: "20-30 minutes",
                      description: "Explore your subconscious patterns",
                      fields: ["Words presented one at a time", "Type first 5 words that come to mind", "Quick, instinctive responses"],
                      tips: ["Respond quickly and instinctively", "Don't overthink", "Write the FIRST words", "Speed matters"]
                    },
                    {
                      phase: 4,
                      title: "Favorite Things & Aspirations",
                      time: "15-20 minutes",
                      description: "Understand your interests and passions",
                      fields: ["10 Favorite Things", "Brief description of each", "Why you like it"],
                      tips: ["Include variety", "Be specific", "Explain WHY it's meaningful", "Quality over quantity"]
                    },
                    {
                      phase: 5,
                      title: "AI Thematic Synthesis & Analysis",
                      time: "Generated by Psychologist",
                      description: "Comprehensive qualitative synthesis and therapeutic techniques",
                      fields: ["14 Points SQIS", "6 AI Thematic Analysis items", "Evidence-Based Therapeutic Techniques"],
                      tips: ["Review Analysis when shared", "Prepare questions", "Follow recommended readings", "Attend video sessions"]
                    }
                  ].map((phase) => (
                    <div key={phase.phase} className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                          {phase.phase}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-blue-900">{phase.title}</h4>
                          <p className="text-sm text-blue-700 mb-2">⏱️ {phase.time}</p>
                          <p className="text-sm text-gray-700 mb-3">{phase.description}</p>
                          <div className="text-sm">
                            <p className="font-semibold text-gray-800 mb-1">What to Expect:</p>
                            <ul className="space-y-1 text-gray-600">
                              {phase.fields.map((field, idx) => (
                                <li key={idx}>• {field}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="text-sm mt-3">
                            <p className="font-semibold text-gray-800 mb-1">Tips:</p>
                            <ul className="space-y-1 text-gray-600">
                              {phase.tips.map((tip, idx) => (
                                <li key={idx}>✓ {tip}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-6 h-6 text-yellow-600" />
                    90-Day Analysis Validity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    Your structured qualitative interpretative summary remains valid for 90 days! This means you can book multiple consultations 
                    without repeating the analysis process.
                  </p>
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded space-y-2">
                    <h4 className="font-semibold text-green-900">Benefits:</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>✓ Book multiple consultations without repeating analysis</li>
                      <li>✓ Different psychologists can view same thematic data</li>
                      <li>✓ Save time on follow-up consultations</li>
                      <li>✓ Validity counter shown on dashboard</li>
                      <li>✓ After 90 days, new analysis required</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Psychologist Guide Tab */}
          <TabsContent value="psychologist">
            <Card>
              <CardHeader>
                <CardTitle>Psychologist Workflow Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg">
                  <h3 className="font-bold text-xl text-indigo-900 mb-4">Clinical Workflow Overview</h3>
                  <div className="space-y-4">
                    {[
                      { step: 1, title: "Review Client Data", time: "30 min", description: "Review all 4 client phases, identify thematic patterns" },
                      { step: 2, title: "Complete 14 Points SQIS", time: "45-60 min", description: "All 14 points required - foundation for structured qualitative interpretative summary" },
                      { step: 3, title: "Generate AI Thematic Analysis", time: "2-3 min", description: "Click 'Generate AI Analysis' - 6 thematic Analysis items auto-created" },
                      { step: 4, title: "Generate Therapeutic Techniques", time: "1-2 min", description: "Click 'Evidence Based Techniques' tab, wait 30-60 seconds" },
                      { step: 5, title: "Share Analysis", time: "5 min", description: "Toggle switches for each Analysis, discuss in session first" },
                      { step: 6, title: "Video Consultation", time: "50 min", description: "Discuss thematic findings, answer questions, provide guidance" }
                    ].map((item) => (
                      <div key={item.step} className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm">
                        <div className="bg-indigo-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold flex-shrink-0">
                          {item.step}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.title}</h4>
                          <p className="text-sm text-indigo-600">⏱️ {item.time}</p>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-lg mb-3">Generating AI Analysis</h3>
                  <div className="space-y-3">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Step 1: Complete 14 Points SQIS</h4>
                      <p className="text-sm text-blue-800">Review Point 1 (auto-generated from Phase 1) and complete Points 2-16 covering recurring response tendencies, challenges, strengths, values, relationships, coping mechanisms, patterns, goals, and clinical impressions.</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">Step 2: Generate AI Thematic Analysis</h4>
                      <p className="text-sm text-green-800 mb-2">After completing synthesis, click "🤖 Generate AI Analysis". System generates 6 comprehensive thematic Analysis items:</p>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>• Client Synthesis (thematic overview)</li>
                        <li>• Recurring response tendencies Themes Analysis</li>
                        <li>• Core Challenges Themes</li>
                        <li>• Strengths & Resources Themes</li>
                        <li>• Belief System Themes</li>
                        <li>• Psychological Patterns Themes</li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2">Step 3: Generate Evidence-Based Therapeutic Techniques</h4>
                      <p className="text-sm text-blue-800 mb-2">Navigate to "📚 Evidence Based Therapeutic Techniques" tab and click "Generate Recommendations". System provides:</p>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• 1-2 carefully selected evidence-based resources</li>
                        <li>• Complete implementation guides for each technique</li>
                        <li>• Therapeutic techniques (3-5 per resource)</li>
                        <li>• Session-by-session breakdown (3-4 sessions)</li>
                        <li>• Client practice assignments (2-3 weeks)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                  <h4 className="font-semibold text-amber-900 mb-2">⚠️ Important Notes</h4>
                  <ul className="text-sm text-amber-800 space-y-1">
                    <li>• Ensure Universal LLM key has sufficient balance</li>
                    <li>• Therapeutic techniques take 30-60 seconds to generate</li>
                    <li>• Page auto-reloads after successful generation</li>
                    <li>• Review all AI-generated content before sharing with client</li>
                    <li>• Discuss thematic findings in session before sharing</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* 5 Phases Tab */}
          <TabsContent value="phases">
            <Card>
              <CardHeader>
                <CardTitle>5-Phase Structured Qualitative Interpretative Summary Process</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-100 to-indigo-100 p-6 rounded-lg">
                    <h3 className="font-bold text-xl text-indigo-900 mb-4">Process Flow</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-600 text-white rounded px-3 py-1 text-sm font-semibold">START</div>
                        <span className="text-gray-700">Client Books Consultation</span>
                      </div>
                      <div className="ml-6 border-l-2 border-blue-300 pl-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-blue-600 border-2 border-blue-600">1</div>
                          <span className="text-gray-700">Phase 1: Basic Details (Client - 15-20 min)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-blue-600 border-2 border-blue-600">2</div>
                          <span className="text-gray-700">Phase 2: Moral Values Analysis (Client - 30-45 min)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-blue-600 border-2 border-blue-600">3</div>
                          <span className="text-gray-700">Phase 3: Response Association (Client - 20-30 min)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-blue-600 border-2 border-blue-600">4</div>
                          <span className="text-gray-700">Phase 4: Favorite Things (Client - 15-20 min)</span>
                        </div>
                        <div className="bg-amber-100 border-l-4 border-amber-500 p-3 rounded">
                          <p className="text-sm text-amber-900">⏳ Client Waiting - Psychologist Notified</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">5</div>
                          <span className="text-gray-700">Phase 5: Thematic Synthesis (Psychologist - 60-90 min)</span>
                        </div>
                        <div className="ml-10 space-y-2 text-sm text-gray-600">
                          <p>→ Complete 14 Points SQIS</p>
                          <p>→ Generate AI Thematic Analysis</p>
                          <p>→ Generate Evidence-Based Therapeutic Techniques</p>
                        </div>
                        <div className="bg-green-100 border-l-4 border-green-500 p-3 rounded">
                          <p className="text-sm text-green-900">✅ Analysis Ready - Client Notified</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="bg-green-600 text-white rounded px-3 py-1 text-sm font-semibold">END</div>
                        <span className="text-gray-700">Video Consultation / Review Session</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Timeline Expectations</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p><strong>Client Data Completion:</strong> 90-120 minutes total</p>
                        <p><strong>Psychologist Structured Qualitative Interpretative Summary:</strong> 60-90 minutes</p>
                        <p><strong>Total Process:</strong> 2.5-3.5 hours per analysis</p>
                      </div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2">Key Points</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Client completes Phases 1-4 independently</li>
                        <li>• Psychologist conducts Phase 5 thematic synthesis</li>
                        <li>• All phases build on each other thematically</li>
                        <li>• Thematic Analysis generated from comprehensive qualitative data</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features">
            <div className="grid gap-6">
              {[
                {
                  title: "14 Points SQIS",
                  icon: FileText,
                  color: "blue",
                  description: "Comprehensive qualitative thematic synthesis framework covering all aspects of client's psychological themes.",
                  features: ["Point 1: Client's Present Condition", "Points 2-16: Recurring response tendencies themes, challenges, strengths, values, relationships, coping patterns, goals"]
                },
                {
                  title: "AI-Generated Thematic Analysis",
                  icon: FileText,
                  color: "purple",
                  description: "Six comprehensive thematic Analysis items generated from qualitative data triangulation.",
                  features: ["AI Thematic Summary", "Recurring response tendencies Themes", "Core Challenge Themes", "Strengths & Resources Themes", "Belief System Themes", "Psychological Pattern Themes"]
                },
                {
                  title: "Evidence-Based Therapeutic Techniques",
                  icon: BookOpen,
                  color: "green",
                  description: "Research-backed therapeutic techniques with detailed implementation guides tailored to client themes.",
                  features: ["1-2 carefully selected evidence-based resources", "Complete implementation guides", "Therapeutic techniques", "Session-by-session breakdown", "Client practice assignments"]
                },
                {
                  title: "90-Day Analysis Validity",
                  icon: Award,
                  color: "yellow",
                  description: "Structured Qualitative Interpretative Summary remains valid for 90 days, enabling multiple consultations without re-analysis.",
                  features: ["Valid for 90 days", "Reuse across consultations", "Different psychologists can access thematic data", "Validity tracking on dashboard"]
                },
                {
                  title: "Video Consultations",
                  icon: Video,
                  color: "red",
                  description: "DPDPA-compliant secure video calls with built-in consent management.",
                  features: ["Secure peer-to-peer connection", "DPDPA consent management", "Call history tracking", "Built-in platform"]
                }
              ].map((feature, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className={`bg-${feature.color}-100 p-2 rounded-lg`}>
                        <feature.icon className={`w-6 h-6 text-${feature.color}-600`} />
                      </div>
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">{feature.description}</p>
                    <div className={`bg-${feature.color}-50 p-3 rounded`}>
                      <ul className={`text-sm text-${feature.color}-800 space-y-1`}>
                        {feature.features.map((f, i) => (
                          <li key={i}>• {f}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Phase 5 AI Features Tab - Comprehensive Documentation */}
          <TabsContent value="phase5-ai">
            <div className="space-y-6">
              {/* Overview Card */}
              <Card className="border-l-4 border-purple-500">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardTitle className="flex items-center gap-2 text-purple-900">
                    <FileText className="w-8 h-8" />
                    Phase 5 AI Features - Complete Guide
                  </CardTitle>
                  <p className="text-sm text-purple-700 mt-2">
                    Comprehensive documentation for all 7 AI-powered clinical analysis features
                  </p>
                </CardHeader>
                <CardContent className="mt-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-bold text-purple-900 mb-3">Core Principles</h3>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li>✅ <strong>Triangulated Data:</strong> All Analysis uses comprehensive data across Phases 1-4</li>
                      <li>✅ <strong>Safety-First:</strong> Automatic risk analysis with location-specific crisis helplines</li>
                      <li>✅ <strong>Evidence-Based:</strong> APA citations, verified research, academic rigor</li>
                      <li>✅ <strong>Controlled Sharing:</strong> Psychologist controls what clients see</li>
                      <li>✅ <strong>Maximum Output:</strong> All features use Gemini 2.5 Pro with 65,536 tokens (~49,000 words)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* 1. Standard AI Reports (6 Reports) */}
              <Card>
                <CardHeader className="bg-blue-50">
                  <CardTitle className="text-blue-900">1. Standard AI Analysis (6 items)</CardTitle>
                  <p className="text-sm text-blue-700">Comprehensive structured qualitative interpretative summary generated automatically from Phase 1-4 data</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-gray-50 p-4 rounded border-l-4 border-blue-500">
                    <h4 className="font-semibold text-blue-900 mb-2">📊 Exploring Client Feelings</h4>
                    <p className="text-sm text-gray-700 mb-2"><strong>Purpose:</strong> Deep analysis of emotional patterns, affective states, and feeling-processing styles</p>
                    <p className="text-sm text-gray-600"><strong>Data Sources:</strong> Phase 2 (moral values), Phase 3 (response associations), Phase 4 (rapid responses)</p>
                    <p className="text-sm text-gray-600"><strong>Output:</strong> ~2,000-3,000 words covering emotional landscape, triggers, coping, therapeutic implications</p>
                    <p className="text-sm text-blue-600 mt-2"><strong>Use Cases:</strong> Emotional dysregulation, affective patterns, emotion-focused therapy</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded border-l-4 border-green-500">
                    <h4 className="font-semibold text-green-900 mb-2">⚖️ Emotional Balance Snapshot</h4>
                    <p className="text-sm text-gray-700 mb-2"><strong>Purpose:</strong> Assess equilibrium between positive/negative emotions, resilience, stability</p>
                    <p className="text-sm text-gray-600"><strong>Analysis:</strong> Valence mapping (positive/negative/neutral), pattern detection, consistency</p>
                    <p className="text-sm text-gray-600"><strong>Output:</strong> Balance score, protective factors, risk factors, stability indicators</p>
                    <p className="text-sm text-green-600 mt-2"><strong>Use Cases:</strong> Quick emotional health snapshot, progress tracking, resilience analysis</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded border-l-4 border-purple-500">
                    <h4 className="font-semibold text-purple-900 mb-2">💪 Strengths and Coping Guide</h4>
                    <p className="text-sm text-gray-700 mb-2"><strong>Purpose:</strong> Identify client strengths, resources, coping mechanisms, development areas</p>
                    <p className="text-sm text-gray-600"><strong>Approach:</strong> Asset-based (not deficit-focused), categorized strengths (cognitive, emotional, social)</p>
                    <p className="text-sm text-gray-600"><strong>Output:</strong> Identified strengths, current coping strategies, untapped resources, growth opportunities</p>
                    <p className="text-sm text-blue-600 mt-2"><strong>Use Cases:</strong> Solution-focused therapy, building self-awareness, resilience enhancement</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded border-l-4 border-orange-500">
                    <h4 className="font-semibold text-blue-900 mb-2">🔍 Deep Dive into Inner World</h4>
                    <p className="text-sm text-gray-700 mb-2"><strong>Purpose:</strong> Comprehensive psychological analysis integrating ALL analysis data</p>
                    <p className="text-sm text-gray-600"><strong>Most Comprehensive:</strong> Uses complete Phases 1-4 data with cross-phase pattern analysis</p>
                    <p className="text-sm text-gray-600"><strong>Output:</strong> Core identity themes, cognitive style, relational patterns, consistent response patterns under emotional elicitation, clinical formulation</p>
                    <p className="text-sm text-blue-600 mt-2"><strong>Use Cases:</strong> Case conceptualization, treatment planning foundation, supervision discussions</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded border-l-4 border-indigo-500">
                    <h4 className="font-semibold text-indigo-900 mb-2">📋 Client Synthesis</h4>
                    <p className="text-sm text-gray-700 mb-2"><strong>Purpose:</strong> Executive summary synthesizing all findings into accessible overview</p>
                    <p className="text-sm text-gray-600"><strong>Sources:</strong> 14 Points SQIS + all 4 previous Analysis items + Phases 1-4 context</p>
                    <p className="text-sm text-gray-600"><strong>Output:</strong> Key findings, core themes, clinical recommendations, next steps, risk summary</p>
                    <p className="text-sm text-indigo-600 mt-2"><strong>Use Cases:</strong> Client feedback sessions, sharing with providers, progress tracking baseline</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded border-l-4 border-pink-500">
                    <h4 className="font-semibold text-pink-900 mb-2">🎨 Metaphorical Client Portrait</h4>
                    <p className="text-sm text-gray-700 mb-2"><strong>Purpose:</strong> Creative narrative representation using metaphors and imagery</p>
                    <p className="text-sm text-gray-600"><strong>Style:</strong> Poetic, symbolic, therapeutic storytelling - bypasses defensiveness</p>
                    <p className="text-sm text-gray-600"><strong>Output:</strong> Opening metaphor, landscape description, journey narrative, symbolic elements</p>
                    <p className="text-sm text-pink-600 mt-2"><strong>Use Cases:</strong> Building alliance, facilitating insight, creative therapy, making patterns accessible</p>
                  </div>

                  <div className="bg-blue-100 p-4 rounded mt-4">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Generation:</p>
                    <p className="text-sm text-blue-800">Click "Generate AI Analysis" → All 6 Analysis items generated sequentially in ~4-5 minutes</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Model:</strong> Gemini 2.5 Pro | <strong>Max Output:</strong> 65,536 tokens (~49,000 words each)</p>
                  </div>
                </CardContent>
              </Card>

              {/* 2. Custom Reports */}
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-green-900">2. Custom Analysis</CardTitle>
                  <p className="text-sm text-green-700">Flexible AI analysis on ANY specific topic or question about the client</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-green-50 p-4 rounded">
                    <h4 className="font-semibold text-green-900 mb-2">✨ Overview</h4>
                    <p className="text-sm text-gray-700">
                      Most flexible feature - generate specialized analysis on ANY topic. Uses TRIANGULATED data from all 6 AI Analysis items + 14 Points SQIS.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">🎯 Common Use Cases (20+ examples):</h4>
                    <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-700">
                      <ul className="space-y-1">
                        <li>• Attachment style analysis</li>
                        <li>• Career readiness analysis</li>
                        <li>• Parenting approaches evaluation</li>
                        <li>• Relationship pattern analysis</li>
                        <li>• Grief processing examination</li>
                        <li>• Trauma response patterns</li>
                        <li>• Spiritual/existential themes</li>
                        <li>• Work stress coping mechanisms</li>
                        <li>• Family dynamics analysis</li>
                        <li>• Self-esteem development</li>
                      </ul>
                      <ul className="space-y-1">
                        <li>• Anger management analysis</li>
                        <li>• Decision-making styles</li>
                        <li>• Communication patterns</li>
                        <li>• Boundary-setting abilities</li>
                        <li>• Conflict resolution approaches</li>
                        <li>• Emotional regulation capacity</li>
                        <li>• Social anxiety patterns</li>
                        <li>• Perfectionism analysis</li>
                        <li>• Identity development</li>
                        <li>• Resilience factors</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-red-50 p-4 rounded border-l-4 border-red-500">
                    <h4 className="font-semibold text-red-900 mb-2">🚨 Automatic Safety Features (MANDATORY):</h4>
                    <p className="text-sm text-red-800 mb-2"><strong>HIGH RISK:</strong> Death/suicide ideation, hopelessness, self-burden themes, method indicators</p>
                    <p className="text-sm text-red-700 mb-2"><strong>MODERATE RISK:</strong> Pervasive negativity (>70%), loss of protective factors, disconnection</p>
                    <p className="text-sm text-red-600"><strong>Auto-Includes:</strong> Location-specific crisis helplines (India: AASRA, USA: 988, International)</p>
                  </div>

                  <div className="bg-blue-100 p-4 rounded">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Technical Details:</p>
                    <p className="text-sm text-blue-800"><strong>Model:</strong> Gemini 2.5 Pro</p>
                    <p className="text-sm text-blue-800"><strong>Max Output:</strong> 65,536 tokens (~49,000 words)</p>
                    <p className="text-sm text-blue-800"><strong>Generation Time:</strong> 60-90 seconds</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Visibility:</strong> Default hidden from client, psychologist can toggle</p>
                  </div>
                </CardContent>
              </Card>

              {/* 3. Ask Buddy */}
              <Card>
                <CardHeader className="bg-blue-50">
                  <CardTitle className="text-purple-900">3. Ask Buddy</CardTitle>
                  <p className="text-sm text-purple-700">AI-powered Q&A assistant with file upload capability</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-blue-50 p-4 rounded">
                    <h4 className="font-semibold text-purple-900 mb-2">🤖 Concept</h4>
                    <p className="text-sm text-gray-700">
                      Think of Ask Buddy as having an AI colleague who has thoroughly studied the complete case file and can answer ANY specific question instantly.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📁 Unique Feature: File Upload & Triangulation</h4>
                    <p className="text-sm text-gray-700 mb-2">Upload external files for cross-referencing:</p>
                    <ul className="text-sm text-gray-600 space-y-1 ml-4">
                      <li>• Therapy session notes</li>
                      <li>• Previous psychiatric evaluations</li>
                      <li>• Neuropsychological analyses</li>
                      <li>• External clinical Analysis</li>
                    </ul>
                    <p className="text-sm text-purple-700 mt-2">
                      <strong>Example:</strong> "How does this neuropsych Analysis align with Phase 3 cognitive patterns?"
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">💡 Use Case Examples:</h4>
                    <div className="space-y-2 text-sm text-gray-700">
                      <p><strong>Quick Questions:</strong> "What evidence is there for emotional dysregulation?"</p>
                      <p><strong>Pattern Analysis:</strong> "Compare client's self-description in Phase 1 with response associations in Phase 3"</p>
                      <p><strong>Treatment Planning:</strong> "What interventions would be most suitable for this client's coping style?"</p>
                      <p><strong>File Integration:</strong> "Integrate findings from this therapy note with INSIGHT data"</p>
                    </div>
                  </div>

                  <div className="bg-blue-100 p-4 rounded">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Technical Details:</p>
                    <p className="text-sm text-blue-800"><strong>Model:</strong> Gemini 2.5 Pro</p>
                    <p className="text-sm text-blue-800"><strong>Context Window:</strong> 128,000 tokens (handles massive data)</p>
                    <p className="text-sm text-blue-800"><strong>Max Output:</strong> 65,536 tokens (~49,000 words)</p>
                    <p className="text-sm text-blue-800"><strong>Generation Time:</strong> 15-60 seconds (90s with file)</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Storage:</strong> All queries saved, visibility controllable</p>
                  </div>
                </CardContent>
              </Card>

              {/* 4. Research Framework Synthesis */}
              <Card>
                <CardHeader className="bg-yellow-50">
                  <CardTitle className="text-yellow-900">4. Research Framework Synthesis</CardTitle>
                  <p className="text-sm text-yellow-700">Applies established psychological theories to explain client's patterns</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-yellow-50 p-4 rounded">
                    <h4 className="font-semibold text-yellow-900 mb-2">🎓 Purpose</h4>
                    <p className="text-sm text-gray-700">
                      Identifies and applies 2-4 established psychological theories/frameworks that explain the client's specific patterns. Bridges academic theory with clinical practice.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">🔬 Academic Database Search (3 sources):</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• <strong>OpenAlex:</strong> 250M scholarly works</li>
                      <li>• <strong>Semantic Scholar:</strong> 200M academic papers</li>
                      <li>• <strong>PubMed:</strong> 35M biomedical citations</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-2">Focus: Theory papers (Beck, Linehan, Joiner, etc.), published 2010-2024</p>
                  </div>

                  <div className="bg-red-50 p-4 rounded border-l-4 border-red-500">
                    <h4 className="font-semibold text-red-900 mb-2">🧠 Critical Features:</h4>
                    <p className="text-sm text-red-800 mb-1"><strong>1. Client-Specific Depth:</strong> Not generic theory - exact behavioral evidence from responses</p>
                    <p className="text-sm text-red-800 mb-1"><strong>2. 41-Second Freeze Analysis:</strong> Phase 4 overwhelm/shutdown patterns analyzed</p>
                    <p className="text-sm text-red-800 mb-1"><strong>3. Suicide Risk Integration:</strong> Joiner's Theory, Cry of Pain Model when indicators present</p>
                    <p className="text-sm text-red-800"><strong>4. Practical Steps:</strong> Concrete intervention actions (not just "use CBT")</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📊 Output Structure:</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Search summary (databases, results)</li>
                      <li>• 2-4 theoretical frameworks with full APA citations</li>
                      <li>• CLIENT-SPECIFIC application for each framework</li>
                      <li>• Behavioral evidence from client data</li>
                      <li>• 41-second freeze analysis (if present)</li>
                      <li>• Suicide risk analysis (if applicable)</li>
                      <li>• Practical application roadmap (Week 1-2, Month 1-3, 3-6 months)</li>
                    </ul>
                  </div>

                  <div className="bg-blue-100 p-4 rounded">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Technical Details:</p>
                    <p className="text-sm text-blue-800"><strong>Model:</strong> Gemini 2.5 Pro</p>
                    <p className="text-sm text-blue-800"><strong>Max Output:</strong> 65,536 tokens (~49,000 words) - INCREASED from 5,000</p>
                    <p className="text-sm text-blue-800"><strong>Generation Time:</strong> 45-90 seconds (includes database searches)</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Use Cases:</strong> Case conceptualization, grounding intuition in theory, treatment modality selection</p>
                  </div>
                </CardContent>
              </Card>

              {/* 5. APA Case Parallels */}
              <Card>
                <CardHeader className="bg-blue-50">
                  <CardTitle className="text-blue-900">5. APA Case Parallels</CardTitle>
                  <p className="text-sm text-blue-700">Searches academic databases for verified case studies matching client's patterns</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-blue-50 p-4 rounded">
                    <h4 className="font-semibold text-blue-900 mb-2">🔎 Purpose</h4>
                    <p className="text-sm text-gray-700">
                      Find similar clinical cases in published research. Identify evidence-based treatment approaches proven effective for similar patterns. Validate clinical conceptualization with empirical literature.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">🔄 4-Level Hierarchical Search Strategy:</h4>
                    <ol className="text-sm text-gray-700 space-y-1 ml-4 list-decimal">
                      <li><strong>Level 1:</strong> Search complete pattern combination (all patterns together)</li>
                      <li><strong>Level 2:</strong> Break down to pattern pairs and critical elements</li>
                      <li><strong>Level 3:</strong> Search treatment literature for primary diagnosis</li>
                      <li><strong>Level 4:</strong> Related constructs and transdiagnostic processes</li>
                    </ol>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📚 Academic Sources (4 databases):</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• <strong>OpenAlex:</strong> 250M works (20 results per query)</li>
                      <li>• <strong>Semantic Scholar:</strong> 200M papers (15 results)</li>
                      <li>• <strong>PubMed:</strong> 35M citations (10 results)</li>
                      <li>• <strong>CrossRef:</strong> DOI verification for all results</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-2"><strong>Filters:</strong> Case studies only, 2015-2024, open access, peer-reviewed</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded border-l-4 border-purple-500">
                    <h4 className="font-semibold text-purple-900 mb-2">⚡ Priority Hierarchy (Safety First):</h4>
                    <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                      <li><strong>Immediate Safety & Risk Analysis</strong> (TOP PRIORITY)</li>
                      <li>Protective Factors & Resilience</li>
                      <li>Evidence-Based Interventions (CBT, DBT, psychodynamic, etc.)</li>
                      <li>Therapeutic Process (alliance, resistance, timing)</li>
                      <li>Prognostic Information (clinical course, outcomes)</li>
                    </ol>
                  </div>

                  <div className="bg-red-50 p-4 rounded border-l-4 border-red-500">
                    <h4 className="font-semibold text-red-900 mb-2">🚫 Strict Exclusions (Bias Prevention):</h4>
                    <p className="text-sm text-red-800">Search explicitly EXCLUDES demographic factors to focus on universal clinical phenomena:</p>
                    <p className="text-sm text-red-700">❌ Age, Gender, Nationality, Ethnicity, Race, Culture, Occupation, Education, Socioeconomic status, Relationship status</p>
                  </div>

                  <div className="bg-blue-100 p-4 rounded">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Technical Details:</p>
                    <p className="text-sm text-blue-800"><strong>Model:</strong> Gemini 2.5 Pro</p>
                    <p className="text-sm text-blue-800"><strong>Max Output:</strong> 65,536 tokens (~49,000 words)</p>
                    <p className="text-sm text-blue-800"><strong>Generation Time:</strong> 60-90 seconds (includes multi-database search)</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Output:</strong> Full APA citations with DOI, direct quotations, clinical relevance, safety implications, therapeutic recommendations</p>
                  </div>
                </CardContent>
              </Card>

              {/* 6. Book Recommendations */}
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-green-900">6. Book Reflections</CardTitle>
                  <p className="text-sm text-green-700">3 public domain self-help books (pre-1929) matched to client's needs</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-green-50 p-4 rounded">
                    <h4 className="font-semibold text-green-900 mb-2">📚 Why Public Domain (Pre-1929)?</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>✅ <strong>Free & Accessible:</strong> No cost to clients</li>
                      <li>✅ <strong>Legal:</strong> No copyright issues</li>
                      <li>✅ <strong>Timeless Wisdom:</strong> Classic psychology, philosophy, self-help</li>
                      <li>✅ <strong>Multiple Formats:</strong> EPUB, TXT, PDF available</li>
                      <li>✅ <strong>Global Access:</strong> Project Gutenberg worldwide</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📖 Common Recommendations:</h4>
                    <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-700">
                      <ul className="space-y-1">
                        <li>• Marcus Aurelius - "Meditations" (Stoicism)</li>
                        <li>• Epictetus - "Enchiridion" (Stoic wisdom)</li>
                        <li>• William James - "Varieties of Religious Experience"</li>
                        <li>• Ralph Waldo Emerson - "Self-Reliance"</li>
                      </ul>
                      <ul className="space-y-1">
                        <li>• Henry David Thoreau - "Walden"</li>
                        <li>• James Allen - "As a Man Thinketh"</li>
                        <li>• Plato - "The Republic"</li>
                        <li>• Seneca - "Letters from a Stoic"</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📋 Detailed Information for Each Book:</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Title, Author, Year, Relevance Score (0-100)</li>
                      <li>• Why Recommended (client-specific)</li>
                      <li>• Two-Minute Summary (key concepts)</li>
                      <li>• Author's Solutions (implementation guide format)</li>
                      <li>• Session Breakdown (chapter-by-chapter plan)</li>
                      <li>• Client Homework (practical exercises)</li>
                      <li>• Specific Chapters (most relevant)</li>
                      <li>• Expected Outcomes (measurable)</li>
                      <li>• Reading Sequence (1, 2, 3)</li>
                      <li>• Download Links (Gutenberg, EPUB, TXT)</li>
                    </ul>
                  </div>

                  <div className="bg-blue-100 p-4 rounded">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Technical Details:</p>
                    <p className="text-sm text-blue-800"><strong>Model:</strong> Gemini 2.5 Pro</p>
                    <p className="text-sm text-blue-800"><strong>Max Output:</strong> 65,536 tokens (~49,000 words)</p>
                    <p className="text-sm text-blue-800"><strong>Data Source:</strong> Client Synthesis only (already synthesizes all phases)</p>
                    <p className="text-sm text-blue-800"><strong>Generation Time:</strong> 45-75 seconds</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Sharing:</strong> Default hidden, psychologist toggles visibility</p>
                  </div>
                </CardContent>
              </Card>

              {/* 7. Movie Recommendations */}
              <Card>
                <CardHeader className="bg-pink-50">
                  <CardTitle className="text-pink-900">7. Movie Reflections</CardTitle>
                  <p className="text-sm text-pink-700">5 therapeutically appropriate films with language-first approach</p>
                </CardHeader>
                <CardContent className="mt-4 space-y-4">
                  <div className="bg-pink-50 p-4 rounded">
                    <h4 className="font-semibold text-pink-900 mb-2">🎬 Language-First Approach</h4>
                    <p className="text-sm text-gray-700 mb-2">
                      Prioritizes films in client's known languages for maximum cultural resonance and accessibility.
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Example:</strong> Client speaks Hindi, Tamil, English → Recommend Bollywood, Tamil cinema, English films first
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📊 Data Sources (Triangulated):</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• <strong>Phase 1:</strong> Language preferences (languages known, read/write/speak skills)</li>
                      <li>• <strong>14 Points SQIS:</strong> All 14 points (truncated to 600 chars per point)</li>
                      <li>• <strong>Client Synthesis:</strong> Comprehensive psychological profile</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">🎯 Therapeutic Matching Examples:</h4>
                    <div className="space-y-2 text-sm text-gray-700">
                      <p><strong>Loss/Grief:</strong> "Manchester by the Sea," "A Monster Calls"</p>
                      <p><strong>Identity/Belonging:</strong> "Moonlight," "The Farewell," "The Namesake"</p>
                      <p><strong>Resilience:</strong> "The Pursuit of Happyness," "Lion"</p>
                      <p><strong>Family Conflict:</strong> "Little Miss Sunshine," "Taare Zameen Par"</p>
                      <p><strong>Mental Health:</strong> "Silver Linings Playbook," "A Beautiful Mind"</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2">📋 Detailed Information for Each Film:</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Title, Director, Year, Country</li>
                      <li>• Language (production + subtitle availability)</li>
                      <li>• Relevance Score (0-100)</li>
                      <li>• Why Recommended (theme connection to client)</li>
                      <li>• Plot Summary</li>
                      <li>• Therapeutic Themes (3-5 tags)</li>
                      <li>• Discussion Questions (for post-viewing)</li>
                      <li>• Emotional Preparation (content warnings)</li>
                      <li>• Where to Watch (streaming suggestions)</li>
                      <li>• Viewing Sequence (1-5)</li>
                    </ul>
                  </div>

                  <div className="bg-blue-100 p-4 rounded">
                    <p className="text-sm text-blue-900 font-semibold mb-2">⚡ Technical Details:</p>
                    <p className="text-sm text-blue-800"><strong>Model:</strong> Gemini 2.5 Pro</p>
                    <p className="text-sm text-blue-800"><strong>Max Output:</strong> 65,536 tokens (~49,000 words)</p>
                    <p className="text-sm text-blue-800"><strong>Generation Time:</strong> 60-90 seconds</p>
                    <p className="text-sm text-blue-700 mt-1"><strong>Sharing:</strong> Default hidden, psychologist toggles visibility</p>
                    <p className="text-sm text-blue-700"><strong>Use Cases:</strong> Cinematherapy between sessions, discussion starters, cultural validation</p>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Comparison Table */}
              <Card className="border-l-4 border-indigo-500">
                <CardHeader className="bg-indigo-50">
                  <CardTitle className="text-indigo-900">Feature Comparison Summary</CardTitle>
                </CardHeader>
                <CardContent className="mt-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-2 text-left">Feature</th>
                          <th className="p-2 text-left">Purpose</th>
                          <th className="p-2 text-left">Model</th>
                          <th className="p-2 text-left">Max Output</th>
                          <th className="p-2 text-left">Gen Time</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700">
                        <tr className="border-t">
                          <td className="p-2 font-semibold">Standard AI Analysis (6)</td>
                          <td className="p-2">Comprehensive structured qualitative interpretative summary</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">4-5 min total</td>
                        </tr>
                        <tr className="border-t bg-gray-50">
                          <td className="p-2 font-semibold">Custom Analysis</td>
                          <td className="p-2">ANY specific topic analysis</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">60-90 sec</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-semibold">Ask Buddy</td>
                          <td className="p-2">Q&A with file upload</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">15-90 sec</td>
                        </tr>
                        <tr className="border-t bg-gray-50">
                          <td className="p-2 font-semibold">Research Framework</td>
                          <td className="p-2">Theory application</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">45-90 sec</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-semibold">APA Case Parallels</td>
                          <td className="p-2">Published case studies</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">60-90 sec</td>
                        </tr>
                        <tr className="border-t bg-gray-50">
                          <td className="p-2 font-semibold">Book Reflections</td>
                          <td className="p-2">Public domain classics</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">45-75 sec</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-semibold">Movie Reflections</td>
                          <td className="p-2">Cinematherapy</td>
                          <td className="p-2">Gemini 2.5 Pro</td>
                          <td className="p-2">65,536 tokens</td>
                          <td className="p-2">60-90 sec</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 bg-indigo-50 p-4 rounded">
                    <p className="text-sm text-indigo-900 font-semibold mb-2">🎯 Key Takeaways:</p>
                    <ul className="text-sm text-indigo-800 space-y-1 ml-4">
                      <li>✅ All features use Gemini 2.5 Pro (unified model)</li>
                      <li>✅ All features use MAXIMUM output tokens (65,536 = ~49,000 words)</li>
                      <li>✅ Comprehensive Analysis 2-3x longer than previous limits</li>
                      <li>✅ Safety-first with automatic risk analysis</li>
                      <li>✅ Evidence-based with APA citations & verified research</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Best Practices Tab */}
          <TabsContent value="best-practices">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Best Practices for Clients</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Before Starting Analysis</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>✓ Set aside 2 hours uninterrupted time</li>
                      <li>✓ Find quiet, private space</li>
                      <li>✓ Use desktop/laptop (not mobile)</li>
                      <li>✓ Ensure stable internet connection</li>
                      <li>✓ Be in right mindset (calm, focused)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">During Thematic Exploration</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>✓ Respond authentically and openly</li>
                      <li>✓ Take breaks if needed (progress saves)</li>
                      <li>✓ Read prompts carefully</li>
                      <li>✓ Provide detailed, reflective responses</li>
                      <li>✓ No "right" or "wrong" answers</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">After Analysis</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>✓ Review thematic Analysis when shared</li>
                      <li>✓ Prepare questions for psychologist</li>
                      <li>✓ Explore recommended therapeutic techniques</li>
                      <li>✓ Complete practice assignments</li>
                      <li>✓ Attend scheduled video sessions</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Best Practices for Psychologists</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Thematic Data Review</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>✓ Review all phases thoroughly</li>
                      <li>✓ Identify recurring themes across phases</li>
                      <li>✓ Note thematic patterns for follow-up</li>
                      <li>✓ Consider cultural and contextual factors</li>
                      <li>✓ Triangulate thematic findings</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Thematic Synthesis Generation</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>✓ Complete 14 Points SQIS first</li>
                      <li>✓ Be thorough and theme-focused</li>
                      <li>✓ Use professional language</li>
                      <li>✓ Provide actionable thematic insights</li>
                      <li>✓ Review AI-generated themes for accuracy</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Client Communication</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>✓ Explain structured qualitative interpretative summary process clearly</li>
                      <li>✓ Discuss thematic findings in session</li>
                      <li>✓ Share thematic Analysis selectively</li>
                      <li>✓ Encourage questions about themes</li>
                      <li>✓ Follow up regularly</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Troubleshooting Tab */}
          <TabsContent value="troubleshooting">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-6 h-6 text-blue-600" />
                    Common Issues & Solutions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    {
                      issue: "Cannot submit Phase 1 form",
                      solutions: [
                        "Check all mandatory fields are filled",
                        "Ensure Date of Birth is past date (not today)",
                        "Verify numeric fields are numbers only",
                        "Check Reason for Consultation is at least 20 characters",
                        "Fill conditional fields when 'Yes' is selected"
                      ]
                    },
                    {
                      issue: "Book recommendations not generating",
                      solutions: [
                        "Ensure 14 Points SQIS is complete",
                        "Ensure AI Thematic Analysis is generated",
                        "Check Universal LLM key has sufficient balance",
                        "Wait 30-60 seconds for generation",
                        "Click 'Evidence Based Therapeutic Techniques' tab first",
                        "Refresh page after generation"
                      ]
                    },
                    {
                      issue: "Video call not working",
                      solutions: [
                        "Grant camera/microphone permissions",
                        "Use Chrome or Firefox browser",
                        "Ensure DPDPA consent is given",
                        "Test camera in browser settings",
                        "Check internet connection"
                      ]
                    },
                    {
                      issue: "Cannot see thematic Analysis",
                      solutions: [
                        "Ensure psychologist has generated thematic Analysis",
                        "Check if Phase 5 synthesis is complete",
                        "Verify psychologist has shared thematic Analysis",
                        "Refresh the page",
                        "Try logging out and back in"
                      ]
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="border-l-4 border-orange-500 bg-blue-50 p-4 rounded">
                      <h4 className="font-semibold text-blue-900 mb-2">Issue: {item.issue}</h4>
                      <p className="text-sm text-orange-800 font-semibold mb-1">Solutions:</p>
                      <ul className="text-sm text-orange-800 space-y-1">
                        {item.solutions.map((solution, i) => (
                          <li key={i}>✓ {solution}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Technical Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded">
                      <h4 className="font-semibold text-gray-900 mb-2">Browser Compatibility</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>✓ Google Chrome (v90+)</li>
                        <li>✓ Mozilla Firefox (v88+)</li>
                        <li>✓ Safari (v14+)</li>
                        <li>✓ Microsoft Edge (v90+)</li>
                        <li>✗ Internet Explorer not supported</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded">
                      <h4 className="font-semibold text-gray-900 mb-2">Device Requirements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>✓ Desktop/Laptop recommended</li>
                        <li>⚠ Tablets may work but not optimal</li>
                        <li>⚠ Mobile not recommended for analyses</li>
                        <li>✓ Webcam + microphone for video calls</li>
                        <li>✓ Minimum 5 Mbps internet speed</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Quick Reference Tab */}
          <TabsContent value="quick-ref">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Client Quick Reference</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg space-y-3 text-sm">
                    <h4 className="font-bold text-indigo-900">STRUCTURED QUALITATIVE INTERPRETATIVE SUMMARY PHASES (90-120 min total)</h4>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Phase 1:</strong> Basic Details (15-20 min)</p>
                      <p><strong>Phase 2:</strong> Moral Values (30-45 min)</p>
                      <p><strong>Phase 3:</strong> Response Association (20-30 min)</p>
                      <p><strong>Phase 4:</strong> Favorite Things (15-20 min)</p>
                      <p><strong>Phase 5:</strong> Thematic Analysis (Psychologist generates)</p>
                    </div>
                    <div className="pt-3 border-t border-indigo-200">
                      <p className="font-semibold text-indigo-900 mb-1">Remember:</p>
                      <ul className="space-y-1">
                        <li>• Be authentic - no right/wrong answers</li>
                        <li>• Take breaks if needed</li>
                        <li>• Structured Qualitative Interpretative Summary valid for 90 days</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Psychologist Quick Reference</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg space-y-3 text-sm">
                    <h4 className="font-bold text-purple-900">STRUCTURED QUALITATIVE INTERPRETATIVE SUMMARY WORKFLOW</h4>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>1.</strong> Review Client Data (30 min)</p>
                      <p><strong>2.</strong> Complete 14 Points SQIS (45-60 min)</p>
                      <p><strong>3.</strong> Generate AI Thematic Analysis (2-3 min)</p>
                      <p><strong>4.</strong> Generate Therapeutic Techniques (1-2 min)</p>
                      <p><strong>5.</strong> Share Thematic Analysis selectively</p>
                      <p><strong>6.</strong> Video Consultation</p>
                    </div>
                    <div className="pt-3 border-t border-purple-200">
                      <p className="font-semibold text-purple-900 mb-1">Key Points:</p>
                      <ul className="space-y-1">
                        <li>• Complete synthesis before AI thematic Analysis</li>
                        <li>• Wait 30-60s for therapeutic techniques</li>
                        <li>• Discuss thematic findings before sharing</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Support & Help</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded">
                    <h4 className="font-semibold text-blue-900 mb-2">For Clients</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Contact your assigned psychologist</li>
                      <li>• Use Help Center in platform</li>
                      <li>• Email: support@insightplatform.com</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded">
                    <h4 className="font-semibold text-purple-900 mb-2">For Psychologists</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Access Admin Panel resources</li>
                      <li>• Technical Support available</li>
                      <li>• Email: support@insightplatform.com</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600 pb-6">
          <p>INSIGHT Platform Training Module v1.0 | January 2025</p>
          <p className="mt-1">For additional help: support@insightplatform.com</p>
        </div>
      </div>
    </div>
  );
};

export default TrainingModule;
